import { Input, Select } from '@/components/ui/FormControls';
import DatePicker from '@/components/common/DatePicker';
import { useCashAccounts } from '@/hooks/useCash';
import { localBusinessDate, salePaymentDraftSchema } from '@klothiq/validation';

export const purchaseInputClass =
  'min-h-11 w-full rounded-xl border border-input bg-background px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-ring disabled:opacity-60';
export interface PurchasePaymentDraft {
  amount: string;
  account_id: string;
  payment_method: string;
  payment_date: string;
  reference_number: string;
  notes: string;
}
export const emptyPurchasePayment = (): PurchasePaymentDraft => ({
  amount: '',
  account_id: '',
  payment_method: 'cash',
  payment_date: localBusinessDate(),
  reference_number: '',
  notes: '',
});
export function checkedPurchasePayment(value: PurchasePaymentDraft, balance: number) {
  const result = salePaymentDraftSchema.safeParse({ ...value, amount: Number(value.amount) });
  if (!result.success)
    throw new Error(
      (result.error.issues[0]?.message || 'Check payment details').replace(
        'receiving account',
        'payment account',
      ),
    );
  if (Math.round(result.data.amount * 100) > Math.round(balance * 100))
    throw new Error('Payment exceeds the remaining balance');
  return result.data;
}
export default function PurchasePaymentFields({
  value,
  onChange,
  disabled = false,
  amountReadOnly = false,
  id = 'purchase-pay',
}: {
  value: PurchasePaymentDraft;
  onChange: (value: PurchasePaymentDraft) => void;
  disabled?: boolean;
  amountReadOnly?: boolean;
  id?: string;
}) {
  const accounts = useCashAccounts();
  const update = (key: keyof PurchasePaymentDraft, next: string) =>
    onChange({ ...value, [key]: next });
  return (
    <fieldset disabled={disabled} className="grid min-w-0 grid-cols-1 gap-3 sm:grid-cols-2">
      <label htmlFor={`${id}-amount`} className="space-y-1 text-sm">
        <span>Amount (₹)</span>
        <Input
          id={`${id}-amount`}
          type="number"
          min="0.01"
          step="0.01"
          inputMode="decimal"
          readOnly={amountReadOnly}
          value={value.amount}
          onChange={(e) => update('amount', e.target.value)}
          className={purchaseInputClass}
          helpLabel="Amount (₹)"
        />
      </label>
      <label htmlFor={`${id}-account`} className="space-y-1 text-sm">
        <span>Account</span>
        <Select
          id={`${id}-account`}
          value={value.account_id}
          onChange={(e) => update('account_id', e.target.value)}
          className={purchaseInputClass}
          helpLabel="Account"
        >
          <option value="">Select account</option>
          {accounts.data?.map((a) => (
            <option key={a.id} value={a.id}>
              {a.name}
            </option>
          ))}
        </Select>
        {accounts.isLoading && (
          <span className="text-xs text-muted-foreground">Loading accounts…</span>
        )}
        {accounts.error && (
          <span role="alert" className="text-xs text-destructive">
            Accounts could not load.{' '}
            <button type="button" onClick={() => void accounts.refetch()} className="underline">
              Retry
            </button>
          </span>
        )}
      </label>
      <label htmlFor={`${id}-method`} className="space-y-1 text-sm">
        <span>Method</span>
        <Select
          id={`${id}-method`}
          value={value.payment_method}
          onChange={(e) => update('payment_method', e.target.value)}
          className={purchaseInputClass}
          helpLabel="Method"
        >
          {['cash', 'upi', 'bank_transfer', 'cheque', 'other'].map((method) => (
            <option key={method} value={method}>
              {method.replace('_', ' ')}
            </option>
          ))}
        </Select>
      </label>
      <label htmlFor={`${id}-date`} className="space-y-1 text-sm">
        <span>Date</span>
        <DatePicker
          id={`${id}-date`}
          value={value.payment_date}
          onChange={(v) => update('payment_date', v || '')}
          clearable={false}
          disabled={disabled}
          helpLabel="Date"
        />
      </label>
      <label htmlFor={`${id}-ref`} className="space-y-1 text-sm">
        <span>Reference (optional)</span>
        <Input
          id={`${id}-ref`}
          maxLength={120}
          value={value.reference_number}
          onChange={(e) => update('reference_number', e.target.value)}
          className={purchaseInputClass}
          helpLabel="Reference (optional)"
        />
      </label>
      <label htmlFor={`${id}-notes`} className="space-y-1 text-sm">
        <span>Notes (optional)</span>
        <Input
          id={`${id}-notes`}
          maxLength={1000}
          value={value.notes}
          onChange={(e) => update('notes', e.target.value)}
          className={purchaseInputClass}
          helpLabel="Notes (optional)"
        />
      </label>
    </fieldset>
  );
}
